import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaBullhorn, FaPlus, FaEye } from "react-icons/fa";
import { getFPOBroadcasts } from "../../services/fpoBroadcast";
import { getUserRole } from "../../helpers/local_storage_helpers";
import PageHeader from "../../components/header/PageHeader";
import SearchableSelect from "../../components/inputFields/SearchableSelect";
import {
    AUDIENCE_OPTIONS, ChannelBadge, AudienceBadge,
    DispatchStatusBadge, NotificationTabs, Pagination,
} from "./shared";

const PAGE_SIZE = 10;

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const FPOBroadcasts = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const channelFilter = searchParams.get("channel") || "app_push";
    const userRole = getUserRole();
    const isAdmin  = userRole === "fpo_admin" || !userRole;

    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);

    const [search, setSearch]                   = useState("");
    const [debouncedSearch, setDebouncedSearch]  = useState("");
    const [audienceFilter, setAudienceFilter]    = useState("all");

    const [page, setPage]   = useState(1);
    const [count, setCount] = useState(0);
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    const loadBroadcasts = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            params.channel = channelFilter;
            if (audienceFilter !== "all") params.audience_type = audienceFilter;

            const res  = await getFPOBroadcasts(params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setBroadcasts(data.results || []);
                setCount(data.count ?? 0);
            } else {
                setError(data?.message || "Failed to load broadcasts.");
            }
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelFilter]);

    useEffect(() => {
        loadBroadcasts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch, channelFilter, audienceFilter]);

    return (
        <div className="w-full pb-12">
            <PageHeader
                title="Broadcasts"
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search broadcast number…"
                actionLabel={isAdmin ? "New broadcast" : undefined}
                actionIcon={FaPlus}
                onAction={isAdmin ? () => navigate("/notifications/new") : undefined}
            >
                <div className="flex items-center flex-wrap gap-3">
                    <NotificationTabs active="broadcasts" />
                </div>

                <div className="w-44">
                    <SearchableSelect
                        value={audienceFilter}
                        onChange={(v) => { setAudienceFilter(v); setPage(1); }}
                        options={[{ value: "all", label: "All Audiences" }, ...AUDIENCE_OPTIONS]}
                        className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white"
                    />
                </div>
            </PageHeader>

            {/* ── Content ── */}
            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading broadcasts…</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <p className="text-red-500 font-medium">{error}</p>
                    <button onClick={loadBroadcasts} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Retry</button>
                </div>
            ) : broadcasts.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center h-56 gap-3 text-center bg-white rounded-2xl border border-gray-100"
                >
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <FaBullhorn className="text-gray-300 text-3xl" />
                    </div>
                    <p className="font-semibold text-gray-500 text-sm">No broadcasts yet</p>
                    {isAdmin && (
                        <button
                            onClick={() => navigate("/notifications/new")}
                            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold flex items-center gap-2"
                        >
                            <FaPlus size={11} /> New Broadcast
                        </button>
                    )}
                </motion.div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-[11px] text-gray-500 uppercase tracking-wide">
                                    <th className="px-5 py-3 font-semibold">Number</th>
                                    <th className="px-5 py-3 font-semibold">Template / Title</th>
                                    <th className="px-5 py-3 font-semibold">Channel</th>
                                    <th className="px-5 py-3 font-semibold">Audience</th>
                                    <th className="px-5 py-3 font-semibold">Recipients</th>
                                    <th className="px-5 py-3 font-semibold">Sent</th>
                                    <th className="px-5 py-3 font-semibold">Status</th>
                                    <th className="px-5 py-3 font-semibold">Created</th>
                                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {broadcasts.map((b) => (
                                        <motion.tr
                                            key={b.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            onClick={() => navigate(`/notifications/${b.id}`)}
                                            className="border-b border-gray-50 last:border-0 odd:bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors cursor-pointer"
                                        >
                                            <td className="px-5 py-3.5 font-semibold text-gray-700">{b.number}</td>
                                            <td className="px-5 py-3.5 text-gray-800">
                                                {b.template?.name || b.title || "—"}
                                            </td>
                                            <td className="px-5 py-3.5"><ChannelBadge channel={b.channel} /></td>
                                            <td className="px-5 py-3.5"><AudienceBadge type={b.audience_type} /></td>
                                            <td className="px-5 py-3.5 text-gray-700">{b.total_recipients}</td>
                                            <td className="px-5 py-3.5 text-gray-700">{b.sent_count}</td>
                                            <td className="px-5 py-3.5"><DispatchStatusBadge sentAt={b.sent_at} /></td>
                                            <td className="px-5 py-3.5 text-gray-500 text-xs">{fmtDate(b.created_at)}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/notifications/${b.id}`); }}
                                                    title="View"
                                                    className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                                                >
                                                    <FaEye size={12} />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {/* Cards (mobile) */}
                    <div className="md:hidden flex flex-col gap-3">
                        <AnimatePresence>
                            {broadcasts.map((b) => (
                                <motion.div
                                    key={b.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    onClick={() => navigate(`/notifications/${b.id}`)}
                                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 cursor-pointer"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="font-semibold text-gray-800 text-sm">{b.number}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{b.template?.name || b.title || "—"}</p>
                                        </div>
                                        <DispatchStatusBadge sentAt={b.sent_at} />
                                    </div>
                                    <div className="flex items-center gap-2 mt-3">
                                        <ChannelBadge channel={b.channel} />
                                        <AudienceBadge type={b.audience_type} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-600">
                                        <div>{b.total_recipients} recipients</div>
                                        <div>{b.sent_count} sent</div>
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-2">Created {fmtDate(b.created_at)}</p>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onPrev={() => setPage((p) => Math.max(1, p - 1))}
                        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                </>
            )}
        </div>
    );
};

export default FPOBroadcasts;
